import React, { useState, useEffect } from 'react';
import { FiCheckCircle, FiAlertTriangle, FiX, FiDatabase, FiLoader, FiServer } from 'react-icons/fi';
import apiService from '../services/api';

const CheckTablesModal = ({ isOpen, onClose, checkResult, onFixComplete }) => {
  const [loading, setLoading] = useState(false);
  const [fixResult, setFixResult] = useState(null);
  const [error, setError] = useState(null);
  const [loadingStep, setLoadingStep] = useState(0);

  // Simulação de progresso visual enquanto carrega
  useEffect(() => {
    if (isOpen && !checkResult) {
      const interval = setInterval(() => {
        setLoadingStep((prev) => (prev + 1) % 5);
      }, 500);
      return () => clearInterval(interval);
    } else {
      setLoadingStep(0);
    }
  }, [isOpen, checkResult]);

  if (!isOpen) return null;

  // Loading State (quando checkResult é null)
  if (!checkResult) {
    const steps = [
      "Conectando ao banco de dados...",
      "Verificando schemas...",
      "Analisando tabelas...",
      "Validando permissões...",
      "Finalizando diagnóstico..."
    ];

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 p-8 flex flex-col items-center text-center">
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
            <div className="relative bg-green-100 dark:bg-green-900/30 p-4 rounded-full text-green-600 dark:text-green-400">
              <FiDatabase className="w-8 h-8 animate-pulse" />
            </div>
          </div>

          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Verificando Tabelas
          </h3>

          <p className="text-gray-500 dark:text-gray-400 mb-6 min-h-[24px]">
            {steps[loadingStep]}
          </p>

          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4 overflow-hidden">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${((loadingStep + 1) / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>
    );
  }

  // Error State (erro na verificação inicial)
  if (checkResult.error) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-red-600 dark:text-red-400 flex items-center gap-2">
              <FiAlertTriangle /> Erro na Verificação
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <FiX size={20} />
            </button>
          </div>
          <p className="text-gray-600 dark:text-gray-300 mb-6">
            {checkResult.error}
          </p>
          <div className="flex justify-end">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 rounded-lg text-gray-700 dark:text-gray-300 font-medium">
              Fechar
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { missing, details, targetDbName } = checkResult;
  const hasMissing = missing && missing.length > 0;

  // Helper para renderizar lista de tabelas por categoria
  const renderTableList = (detailsObj) => {
    if (!detailsObj) return null;

    // Se for estrutura antiga (plana), converte para formato unificado
    let categories = {};
    if (detailsObj.mariadb && (detailsObj.supabase || detailsObj.app_db)) {
      categories = detailsObj; // Já está no formato novo
    } else {
      categories = { 'Geral': detailsObj }; // Formato legado
    }

    return Object.entries(categories).map(([category, tables]) => {
      if (!tables || Object.keys(tables).length === 0) return null;

      const categoryTitle = {
        'app_db': 'Banco Principal (Sistema)',
        'mariadb': 'Schema do Usuário (MariaDB)',
        'supabase': 'Supabase (PostgreSQL)',
        'Geral': 'Tabelas'
      }[category] || category;

      return (
        <div key={category} className="mb-4 last:mb-0">
          <div className="bg-gray-100 dark:bg-gray-700/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300 uppercase tracking-wider flex items-center gap-2">
            <FiServer className="w-3 h-3" />
            {categoryTitle}
          </div>
          <div className="divide-y divide-gray-100 dark:divide-gray-700 border-l border-r border-b border-gray-200 dark:border-gray-700 rounded-b-lg bg-white dark:bg-gray-800">
            {Object.entries(tables).map(([table, status]) => {
              // Normalize status (pode ser boolean ou objeto detalhado)
              const isOk = typeof status === 'object' ? status.ok : status;
              const reason = typeof status === 'object' ? status.reason : null;
              const missingColumns = typeof status === 'object' ? status.missing : [];

              return (
                <div key={table} className="px-4 py-2 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <div className="flex flex-col overflow-hidden pr-4">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300 font-mono truncate" title={table}>
                      {table}
                    </span>
                    {!isOk && reason === 'missing_columns' && missingColumns?.length > 0 && (
                      <span className="text-[10px] text-red-500 truncate mt-0.5" title={`Colunas faltantes: ${missingColumns.join(', ')}`}>
                        Faltam colunas: {missingColumns.join(', ')}
                      </span>
                    )}
                  </div>

                  {isOk ? (
                    <span className="flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold text-green-600 bg-green-50 dark:bg-green-900/20 dark:text-green-400 px-2 py-0.5 rounded-full uppercase tracking-wide border border-green-100 dark:border-green-900/30">
                      <FiCheckCircle className="w-3 h-3" /> OK
                    </span>
                  ) : (
                    <span className="flex-shrink-0 flex items-center gap-1.5 text-[10px] font-bold text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400 px-2 py-0.5 rounded-full uppercase tracking-wide border border-red-100 dark:border-red-900/30" title={reason === 'missing_columns' ? 'Tabela existe mas faltam colunas' : 'Tabela não encontrada'}>
                      <FiX className="w-3 h-3" /> {reason === 'missing_columns' ? 'Erro Estrutura' : 'Ausente'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    });
  };

  const handleFix = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.fixTables(checkResult.userId);
      setFixResult(res);
      if (onFixComplete) onFixComplete(res);
    } catch (err) {
      setError(err.message || 'Erro ao corrigir tabelas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg border border-gray-200 dark:border-gray-700 flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${hasMissing ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400' : 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400'}`}>
              <FiDatabase className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Verificação de Banco de Dados
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Banco: {targetDbName}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <FiX size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar">

          {fixResult ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <FiCheckCircle className="w-6 h-6 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Correção Concluída!</h3>
                  <p className="text-sm mt-1 opacity-90">As tabelas foram criadas com sucesso.</p>
                </div>
              </div>

              {fixResult.fixed && fixResult.fixed.length > 0 && (
                <div className="text-sm space-y-2">
                  <p className="font-medium text-gray-700 dark:text-gray-300">Ações realizadas:</p>
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1">
                    {fixResult.fixed.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}

              {fixResult.errors && fixResult.errors.length > 0 && (
                <div className="text-sm space-y-2 mt-4">
                  <p className="font-medium text-red-600 dark:text-red-400">Erros encontrados:</p>
                  <ul className="list-disc list-inside text-red-500 dark:text-red-400 space-y-1">
                    {fixResult.errors.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              {hasMissing ? (
                <div className="space-y-6">
                  <div className="flex items-start gap-3 text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                    <FiAlertTriangle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                    <div>
                      <h3 className="font-medium">Tabelas Faltantes Detectadas</h3>
                      <p className="text-sm mt-1 opacity-90">
                        Algumas tabelas necessárias não foram encontradas no banco de dados.
                        Isso pode afetar o funcionamento do sistema.
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Relatório de Tabelas Detalhado
                    </div>
                    <div className="max-h-[300px] overflow-y-auto custom-scrollbar p-2 bg-gray-50/50 dark:bg-gray-900/20">
                      {renderTableList(details)}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-center py-6">
                  <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4 text-green-600 dark:text-green-400">
                    <FiCheckCircle size={32} />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Tudo Certo!
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-xs mx-auto mb-6">
                    Todas as tabelas necessárias estão presentes no banco de dados.
                  </p>

                  <div className="w-full border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden text-left mt-4">
                    <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tabelas Verificadas
                    </div>
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-2 bg-gray-50/50 dark:bg-gray-900/20">
                      {renderTableList(details)}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm flex items-start gap-2">
              <FiAlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            {fixResult || !hasMissing ? 'Fechar' : 'Cancelar'}
          </button>

          {hasMissing && !fixResult && (
            <button
              onClick={handleFix}
              disabled={loading}
              className={`px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg shadow-sm shadow-green-600/20 transition-all flex items-center gap-2 ${loading ? 'opacity-70 cursor-wait' : ''}`}
            >
              {loading ? (
                <>
                  <FiLoader className="animate-spin" />
                  Corrigindo...
                </>
              ) : (
                <>
                  <FiDatabase />
                  Criar Tabelas Faltantes
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheckTablesModal;